import { q1TutorialFunctions } from "./q1-core-functions";


const STATUS_ICONS = {
  completed: 'task_alt',
  updated: 'info',
  open: 'info',
};

export class TutorialManager {

  constructor(currentMember, doQuery = true) {
    // Create loadingComplete promise even if there's no query yet, for consistency
    this._resetLoadingParams();

    this.currentMember = currentMember;
    this.allTutorials = [];
    this.allTutorialsBySection = [];
    this.allTutorialsByKey = {};

    if (doQuery) this.queryTutorials();
    else {
      this.loading = false;
      this._resolveLoading();
    }
  }

  _resetLoadingParams() {
    this.loading = true;
    this.loadingComplete = new Promise((resolve, reject) => {
      this._resolveLoading = resolve;
      this._rejectLoading = reject;
    });
  }

  /**
   * Returns all tutorials in the specified tag and completion stats.
   * Returns null if there are no tutorials in this group.
   * 
   * @param {string} tag - The tag from the tags array
   * 
   * @returns {Object} w/ keys:
   *   - `tutorials`: Array of tutorials in this tag.
   *   - `tutorialCount`: Number of tutorials in this tag.
   *   - `completedCount`: Number of tutorials completed.
   *   - `openCount`: Number of tutorials NOT completed.
   *   - `allCompleted`: Boolean. True if all are completed.
   */
  getTutorialsByTag(tag) {
    const tutorials = this.allTutorials.filter(t => t.tags.includes(tag));
    if (!tutorials.length) return null;

    const completedTutorials = tutorials.filter(t => t.isCompleted);

    return {
      tutorials,
      tutorialCount: tutorials.length,
      completedCount: completedTutorials.length,
      openCount: tutorials.length - completedTutorials.length,
      allCompleted: (completedTutorials.length === tutorials.length),
    };
  }

  async queryTutorials() {
    if (!this.loading) this._resetLoadingParams();

    const { getAllTutorialsBySection } = q1TutorialFunctions;

    this.allTutorials = [];
    this.allTutorialsBySection = [];
    this.allTutorialsByKey = {};

    try {
      const tutorialResponse = await getAllTutorialsBySection({});
      const { data: { allTutorialsBySection }} = tutorialResponse;

      this.allTutorialsBySection = allTutorialsBySection.map(section => ({
        title: section.title,
        tutorials: section.tutorials.map(rawTutorial => this._enrichTutorial(rawTutorial)),
      }));

      this.allTutorials = this.allTutorialsBySection.map(section => section.tutorials).flat();
      this.allTutorialsByKey = Object.fromEntries(
        this.allTutorials.map(tutorial => ([tutorial.key, tutorial]))
      );

      this.loading = false;
      this._resolveLoading();
    } catch(error) {
      console.log('ERROR querying tutorials:', error);
      this.loading = false;
      this._rejectLoading(error);
    }
  }

  async trackTutorialView(tutorialKey) {
    const { trackView } = q1TutorialFunctions;

    const tutorial = this.allTutorialsByKey[tutorialKey];
    if (!tutorial) throw Error(`Invalid tutorialKey: ${tutorialKey}`);

    return trackView({ tutorialKey });
  }

  async markTutorialCompleted(tutorialKey) {
    const { markCompleted } = q1TutorialFunctions;

    const tutorial = this.allTutorialsByKey[tutorialKey];
    if (!tutorial) throw Error(`Invalid tutorialKey: ${tutorialKey}`);

    try {
      const result = await markCompleted({ tutorialKey });

      // Mark the tutorial as completed (thus updating all instance variables)
      tutorial.isCompleted = true;
      tutorial.completedAt = new Date();
      tutorial.completedAtString = tutorial.completedAt?.toLocaleDateString() || '';
      tutorial.completedVersion = tutorial.version;
      tutorial.statusIcon = STATUS_ICONS.completed;
      tutorial.statusMessage = `Completed ${tutorial.completedAtString}`;

      return result;
    } catch (error) {
      throw error;
    }
  }

  _estimateReadingTime(readableText) {
    if (!readableText?.trim().length) return '0 mins';

    const wordCount = readableText.trim().split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / 235);

    return `${readingTime} mins`;
  }

  /**
   * Takes a tutorial from the backend and enriches it with frontend specific properties and 
   * context from the current member.
   */
  _enrichTutorial(rawTutorial) {
    const tutorial = {
      ...rawTutorial,
      ...(this.currentMember?.tutorialState?.[rawTutorial.key] || {}),
    };

    tutorial.readingTime = this._estimateReadingTime(tutorial.content);

    tutorial.completedAt = tutorial.completedAt ? new Date(tutorial.completedAt) : null;
    tutorial.lastViewedAt = tutorial.lastViewedAt ? new Date(tutorial.lastViewedAt) : null;
    tutorial.versionPublishDate = tutorial.versionPublishDate
      ? new Date(tutorial.versionPublishDate)
      : null;

    tutorial.completedAtString = tutorial.completedAt?.toLocaleDateString() || '';
    tutorial.lastViewedAtString = tutorial.lastViewedAt?.toLocaleDateString() || '';
    tutorial.versionPublishDateString = tutorial.versionPublishDate?.toLocaleDateString() || '';
    
    tutorial.isCompleted = tutorial.completedAt
      && (tutorial.completedVersion >= tutorial.version);
    tutorial.isUpdatedSinceCompletion = tutorial.completedAt
      && (tutorial.completedVersion < tutorial.version);
    tutorial.statusIcon = tutorial.isCompleted
      ? STATUS_ICONS.completed
      : (tutorial.isUpdatedSinceCompletion ? STATUS_ICONS.updated : STATUS_ICONS.open);
    
    let sm; // shortcut for statusMessage
    if (tutorial.isCompleted) sm = `Completed ${tutorial.completedAtString}`;
    else if (tutorial.isUpdatedSinceCompletion) sm = 'Updated Since Completion';
    else if (tutorial.lastViewedAt) sm = `Last Viewed ${tutorial.lastViewedAtString}`;
    else sm = 'New to You';
    tutorial.statusMessage = sm;

    tutorial.content = [
      tutorial.content?.trim(),
      `*Version ${tutorial.version}. `
        + `Published ${tutorial.versionPublishDateString}.*`,
    ].join("\n\n---\n\n");

    return tutorial;
  }

}
